import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Copy, Edit, Lock, Plus, RefreshCw, Search, Shield, Terminal, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import DataTable from '../components/DataTable';
import EmptyState from '../components/EmptyState';
import LoadingSkeleton from '../components/LoadingSkeleton';
import SeverityBadge from '../components/SeverityBadge';
import StatusBadge from '../components/StatusBadge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { employeesApi, analyticsApi, teamsApi, enrollmentApi } from '../lib/api';

function riskToSeverity(level) {
  if (level === 'critical' || level === 'high' || level === 'medium' || level === 'low') {
    return level;
  }
  return 'info';
}

export default function Employees() {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [teams, setTeams] = useState([]);
  const [riskScores, setRiskScores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [showAdd, setShowAdd] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', custom_id: '', team_id: '' });
  const [showEnroll, setShowEnroll] = useState(false);
  const [enrollEmployee, setEnrollEmployee] = useState(null);
  const [showEdit, setShowEdit] = useState(false);
  const [editEmployee, setEditEmployee] = useState(null);
  const [activeTokens, setActiveTokens] = useState([]);
  const pageSize = 12;

  const loadEmployees = async () => {
    setLoading(true);
    setError('');
    try {
      const [employeesResponse, teamsResponse, riskResponse] = await Promise.all([
        employeesApi.list({ page, page_size: pageSize }),
        teamsApi.list(),
        analyticsApi.riskScores().catch(() => ({ data: { items: [] } })),
      ]);
      setEmployees(employeesResponse.data?.items || []);
      setTotal(employeesResponse.data?.total || 0);
      setTeams(teamsResponse.data?.items || []);
      setRiskScores(riskResponse.data?.items || []);
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to load employees.';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const fetchActiveTokens = async () => {
    try {
      const response = await enrollmentApi.list({ is_active: true });
      setActiveTokens(response.data?.items || []);
    } catch (err) {
      console.error('Failed to load tokens:', err);
    }
  };

  useEffect(() => {
    loadEmployees();
    fetchActiveTokens();
  }, [page]);

  const handleCreate = async (event) => {
    event.preventDefault();
    setCreating(true);
    try {
      await employeesApi.create({
        name: form.name,
        email: form.email,
        custom_id: form.custom_id || null,
        team_id: form.team_id || null,
      });
      toast.success('Employee created.');
      setShowAdd(false);
      setForm({ name: '', email: '', custom_id: '', team_id: '' });
      loadEmployees();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create employee.');
    } finally {
      setCreating(false);
    }
  };

  const riskMap = useMemo(
    () =>
      riskScores.reduce((accumulator, item) => {
        accumulator[item.employee_id] = item;
        return accumulator;
      }, {}),
    [riskScores]
  );

  const filteredEmployees = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return employees;
    return employees.filter((employee) =>
      [employee.name, employee.email, employee.custom_id, employee.team_name].some((value) =>
        String(value || '').toLowerCase().includes(query)
      )
    );
  }, [employees, search]);

  return (
    <div className="min-h-screen space-y-6 bg-slate-900 p-6 text-slate-300" data-testid="employees-page">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Employees</h1>
          <p className="mt-2 text-slate-500">
            Manage monitored personnel, team assignment, and current behavioral risk visibility.
          </p>
        </div>
        <Button className="bg-blue-600 text-white hover:bg-blue-700" onClick={() => setShowAdd(true)}>
          <Plus className="h-4 w-4" />
          Add Employee
        </Button>
      </div>

      <Card className="border-slate-700 bg-slate-800 shadow-lg">
        <CardHeader className="pb-0">
          <CardTitle className="text-white">Employee Directory</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search by name, email, employee id, or department"
              className="w-full rounded-lg border border-slate-600 bg-slate-700 py-3 pl-10 pr-4 text-white placeholder:text-slate-500 outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {error ? (
            <div className="rounded-lg border border-red-500/20 bg-red-500/10 p-4 text-sm text-red-300">
              {error}
            </div>
          ) : null}

          {loading ? (
            <LoadingSkeleton type="table" />
          ) : filteredEmployees.length ? (
            <DataTable
              columns={[
                { key: 'name', label: 'Employee' },
                { 
                  key: 'custom_id', 
                  label: 'Employee ID', 
                  render: (v, row) => v || <span className="text-[10px] opacity-50 font-mono">{row.id.slice(0, 8)}...</span>
                },
                { key: 'email', label: 'Email' },
                {
                  key: 'team_name',
                  label: 'Department',
                  render: (value) => value || 'Unassigned',
                },
                {
                  key: 'status',
                  label: 'Status',
                  render: (_, row) => (
                    <StatusBadge status={row.online_devices > 0 ? 'online' : 'offline'} />
                  ),
                },
                {
                  key: 'risk',
                  label: 'Risk Level',
                  render: (_, row) => (
                    <SeverityBadge
                      severity={riskToSeverity(riskMap[row.id]?.risk_level || 'info')}
                    />
                  ),
                },
                {
                  key: 'legal_hold',
                  label: 'Legal Hold',
                  render: (value) =>
                    value ? (
                      <span className="inline-flex items-center gap-2 text-amber-400">
                        <Lock className="h-4 w-4" />
                        On
                      </span>
                    ) : (
                      <span className="text-slate-500">Off</span>
                    ),
                },
                {
                  key: 'actions',
                  label: 'Actions',
                  render: (_, row) => (
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-slate-600 bg-slate-900 text-slate-300 hover:bg-slate-700"
                        onClick={(event) => {
                          event.stopPropagation();
                          navigate(`/employees/${row.id}`);
                        }}
                      >
                        View Detail
                      </Button>
                      <Button
                        size="sm"
                        className="bg-blue-600 text-white hover:bg-blue-700"
                        onClick={(event) => {
                          event.stopPropagation();
                          setEnrollEmployee(row);
                          setShowEnroll(true);
                        }}
                      >
                        <Shield className="h-4 w-4" />
                        Enroll
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-slate-600 bg-slate-900 text-slate-300 hover:bg-slate-700"
                        onClick={(event) => {
                          event.stopPropagation();
                          setEditEmployee(row);
                          setShowEdit(true);
                        }}
                      >
                        <Edit className="h-4 w-4" />
                        Edit
                      </Button>
                    </div>
                  ),
                },
              ]}
              data={filteredEmployees}
              loading={loading}
              emptyMessage="No employees match the current search."
              pagination={{
                page,
                pageSize,
                total,
                onPageChange: setPage,
              }}
              onRowClick={(row) => navigate(`/employees/${row.id}`)}
            />
          ) : (
            <EmptyState
              icon={<Users className="h-8 w-8" />}
              title="No employees found"
              description="Adjust the search or create a new employee record."
            />
          )}
        </CardContent>
      </Card>

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="border-slate-700 bg-slate-800 text-slate-300">
          <DialogHeader>
            <DialogTitle className="text-white">Add Employee</DialogTitle>
          </DialogHeader>
          <form className="space-y-4" onSubmit={handleCreate}>
            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase text-slate-500 text-[10px] tracking-wider">Full Name</label>
              <input
                value={form.name}
                onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
                placeholder="Full name"
                className="w-full rounded-lg border border-slate-600 bg-slate-700 px-4 py-3 text-white placeholder:text-slate-500 outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase text-slate-500 text-[10px] tracking-wider">Email Address</label>
              <input
                type="email"
                value={form.email}
                onChange={(event) => setForm((current) => ({ ...current, email: event.target.value }))}
                placeholder="Email address"
                className="w-full rounded-lg border border-slate-600 bg-slate-700 px-4 py-3 text-white placeholder:text-slate-500 outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase text-slate-500 text-[10px] tracking-wider">Employee ID (Optional)</label>
              <input
                value={form.custom_id}
                onChange={(event) => setForm((current) => ({ ...current, custom_id: event.target.value }))}
                placeholder="e.g. TSN/01234"
                className="w-full rounded-lg border border-slate-600 bg-slate-700 px-4 py-3 text-white placeholder:text-slate-500 outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase text-slate-500 text-[10px] tracking-wider">Department</label>
              <select
                value={form.team_id}
                onChange={(event) => setForm((current) => ({ ...current, team_id: event.target.value }))}
                className="w-full rounded-lg border border-slate-600 bg-slate-700 px-4 py-3 text-white outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">No Department</option>
                {teams.map((team) => (
                  <option key={team.id} value={team.id}>
                    {team.name}
                  </option>
                ))}
              </select>
            </div>
            <Button className="w-full bg-blue-600 text-white hover:bg-blue-700" disabled={creating} type="submit">
              {creating ? 'Creating...' : 'Create Employee'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <EnrollmentDialog
        employee={enrollEmployee}
        open={showEnroll}
        tokens={activeTokens}
        onOpenChange={setShowEnroll}
      />

      <EditEmployeeDialog
        employee={editEmployee}
        open={showEdit}
        teams={teams}
        onOpenChange={setShowEdit}
        onSuccess={loadEmployees}
      />
    </div>
  );
}

function EditEmployeeDialog({ employee, open, onOpenChange, teams = [], onSuccess }) {
  const [formData, setFormData] = useState({ name: '', email: '', custom_id: '', team_id: '' });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (employee) {
      setFormData({
        name: employee.name || '',
        email: employee.email || '',
        custom_id: employee.custom_id || '',
        team_id: employee.team_id || '',
      });
    }
  }, [employee]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await employeesApi.update(employee.id, formData);
      toast.success('Employee updated');
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update employee');
    } finally {
      setLoading(false);
    }
  };

  if (!employee) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-slate-700 bg-slate-800 text-slate-300">
        <DialogHeader>
          <DialogTitle className="text-white">Edit Employee: {employee.name}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 pt-4">
          <div className="space-y-2">
            <label className="text-xs font-semibold uppercase text-slate-500 text-[10px] tracking-wider">Full Name</label>
            <input
              value={formData.name}
              onChange={(e) => setFormData(f => ({ ...f, name: e.target.value }))}
              className="w-full rounded-lg border border-slate-700 bg-slate-900 px-4 py-3 text-white outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
          <div className="space-y-2">
            <label className="text-xs font-semibold uppercase text-slate-500 text-[10px] tracking-wider">Email Address</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData(f => ({ ...f, email: e.target.value }))}
              className="w-full rounded-lg border border-slate-700 bg-slate-900 px-4 py-3 text-white outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
          <div className="space-y-2">
            <label className="text-xs font-semibold uppercase text-slate-500 text-[10px] tracking-wider">Employee ID (Optional)</label>
            <input
              value={formData.custom_id}
              onChange={(e) => setFormData(f => ({ ...f, custom_id: e.target.value }))}
              className="w-full rounded-lg border border-slate-700 bg-slate-900 px-4 py-3 text-white outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g. TSN/01234"
            />
          </div>
          <div className="space-y-2">
            <label className="text-xs font-semibold uppercase text-slate-500 text-[10px] tracking-wider">Department</label>
            <select
              value={formData.team_id}
              onChange={(e) => setFormData(f => ({ ...f, team_id: e.target.value }))}
              className="w-full rounded-lg border border-slate-700 bg-slate-900 px-4 py-3 text-white outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">No Department</option>
              {teams.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>
          <Button type="submit" className="w-full bg-blue-600 text-white hover:bg-blue-700" disabled={loading}>
            {loading ? 'Saving...' : 'Save Changes'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EnrollmentDialog({ employee, open, onOpenChange, tokens = [] }) {
  const [selectedToken, setSelectedToken] = useState('new');
  const [oneTimeToken, setOneTimeToken] = useState('');
  const [apiUrl, setApiUrl] = useState('');
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (open && !apiUrl) {
      const current = window.location.origin;
      // Try to guess the API URL based on common patterns or previous usage
      if (current.includes('localhost:3000')) {
        setApiUrl('http://192.168.2.82:1100');
      } else if (current.includes(':3001')) {
        setApiUrl(current.replace(':3001', ':1100'));
      } else {
        setApiUrl(current);
      }
    }
  }, [open, apiUrl]);

  if (!employee) return null;

  const handleGenerateToken = async () => {
    setGenerating(true);
    try {
      const res = await enrollmentApi.create({ max_uses: 1, expires_hours: 24 });
      setOneTimeToken(res.data.token);
      setSelectedToken('new');
      toast.success('Generated new one-time token');
    } catch (err) {
      toast.error('Failed to generate token');
    } finally {
      setGenerating(false);
    }
  };

  const currentToken = selectedToken === 'new' ? oneTimeToken : selectedToken;
  const isPrefix = selectedToken !== 'new' && currentToken.includes('...');
  const command = `ASMAgent.exe --enroll ${apiUrl} ${currentToken || 'TOKEN'} ${employee.id}`;

  const copyToClipboard = (text, message) => {
    navigator.clipboard.writeText(text);
    toast.success(message);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl border-slate-700 bg-slate-800 text-slate-300">
        <DialogHeader>
          <DialogTitle className="text-white">Enroll Device: {employee.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-5 pt-2">
          {/* Section 1: Connection & Identity */}
          <div className="space-y-4 rounded-xl border border-slate-700/50 bg-slate-900/50 p-4">
            <div className="space-y-1.5">
              <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">API Server URL</label>
              <input
                value={apiUrl}
                onChange={(e) => setApiUrl(e.target.value)}
                placeholder="http://192.168.2.82:1100"
                className="w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-blue-400 outline-none focus:ring-1 focus:ring-blue-500/50"
              />
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Employee ID (UUID)</label>
                <div className="flex h-10 items-center gap-2 rounded-lg border border-slate-700 bg-slate-900 px-3 py-2">
                  <code className="flex-1 overflow-hidden text-ellipsis text-[11px] text-slate-400">{employee.id}</code>
                  <Button size="icon" variant="ghost" className="h-6 w-6 text-slate-500 hover:text-white" onClick={() => copyToClipboard(employee.id, 'Employee ID copied')}>
                    <Copy className="h-3 w-3" />
                  </Button>
                </div>
              </div>
              <div className="space-y-1.5">
                <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Enrollment Token</label>
                <div className="flex gap-2">
                  <select
                    value={selectedToken}
                    onChange={(e) => setSelectedToken(e.target.value)}
                    className="h-10 flex-1 rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-[11px] text-white outline-none focus:ring-1 focus:ring-blue-500/50"
                  >
                    <option value="new">
                      {oneTimeToken ? `New: ${oneTimeToken.slice(0, 8)}...` : 'Generate New Token'}
                    </option>
                    {tokens.map((t) => (
                      <option key={t.id} value={t.token_prefix}>
                        Existing: {t.token_prefix}
                      </option>
                    ))}
                  </select>
                  <Button
                    variant="outline"
                    className="h-10 w-10 border-slate-700 bg-slate-900 p-0 text-slate-400 hover:bg-slate-700 hover:text-white"
                    onClick={handleGenerateToken}
                    disabled={generating}
                  >
                    <RefreshCw className={`h-4 w-4 ${generating ? 'animate-spin' : ''}`} />
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Section 2: Command */}
          <div className="space-y-2">
            <div className="flex items-center justify-between px-1">
              <label className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-slate-500">
                <Terminal className="h-3 w-3 text-blue-500" />
                PowerShell Command
              </label>
              <Button size="sm" variant="link" className="h-auto p-0 text-[11px] text-blue-400 hover:text-blue-300" onClick={() => copyToClipboard(command, 'Enrollment command copied')}>
                Copy Command
              </Button>
            </div>
            <div className="relative group">
              <pre className="overflow-x-auto rounded-xl border border-blue-500/20 bg-slate-950 p-4 font-mono text-[11px] leading-relaxed text-amber-300 shadow-inner">
                {command}
              </pre>
              {isPrefix && (
                <div className="mt-2 flex items-center gap-2 rounded-lg bg-red-500/10 px-3 py-2 text-[10px] text-red-400">
                  <AlertTriangle className="h-3 w-3" />
                  Existing tokens only show prefixes. Generate a NEW token for a valid command.
                </div>
              )}
            </div>
          </div>

          {/* Section 3: Help */}
          <div className="rounded-xl border border-blue-500/10 bg-blue-500/5 p-4 text-[11px] leading-relaxed text-blue-300/80">
            <p className="mb-2 font-bold text-blue-300">Quick Start:</p>
            <ol className="list-inside list-decimal space-y-1">
              <li>Open PowerShell as **Administrator**.</li>
              <li>Navigate to the folder containing `ASMAgent.exe`.</li>
              <li>Paste the command above and run.</li>
            </ol>
            <div className="mt-3 pt-3 border-t border-blue-500/10 text-[9px] uppercase tracking-tighter opacity-50">
               Formats: URL (String) • Token (Hex) • ID (UUID)
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
